import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Pagination, Row, Table } from 'react-bootstrap';
import axios from 'axios';
import { externalUrl } from './externalUrl';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/api`;

// Shown until the real list loads from the database (which also includes
// every industry ever typed/pasted in, not just these defaults).
const DEFAULT_INDUSTRY_OPTIONS = [
  'Restaurant / Food / Bar',
  'Hospitality',
  'Entertainment',
  'Tech',
  'Finance',
  'Plumbing / Electricity / HVAC',
  'Marketing',
  'Cars',
  'Real Estate',
  'Property Maintenance',
  'Wholesale',
  'Beauty / Hair',
  'Healthcare',
  'Construction'
];

export type Lead = {
  _id: string;
  businessName?: string;
  contactName?: string;
  email?: string;
  emailNotFound?: boolean;
  emailNotFoundAt?: string;
  phone?: string;
  instagram?: string;
  instagramNotFound?: boolean;
  instagramNotFoundAt?: string;
  website?: string;
  outdatedWebsite?: boolean;
  underConstructionWebsite?: boolean;
  city?: string;
  industry?: string;
  notes?: string;
  googleBusinessUrl?: string;
  inbound: boolean;
  source?: 'outbound' | 'mockup_form' | 'newsletter' | 'free_audit_form';
  coldEmailSent?: boolean;
  coldEmailSentAt?: string;
  coldEmailHtml?: string;
  coldEmailSubject?: string;
  coldEmailVariant?: 'mockup' | 'marketing';
  coldEmailOpened?: boolean;
  coldEmailClicked?: boolean;
  coldEmailResentAt?: string;
  reminderEmailSent?: boolean;
  reminderEmailSentAt?: string;
  outdatedMockupSent?: boolean;
  outdatedMockupSentAt?: string;
  outdatedMockupOpened?: boolean;
  outdatedMockupClicked?: boolean;
  closedWebDevClient?: boolean;
  closedWebDevClientAt?: string;
  onboardingSent?: boolean;
  onboardingSentAt?: string;
  onboardingOpened?: boolean;
  onboardingClicked?: boolean;
  onboardingReminderSent?: boolean;
  onboardingReminderSentAt?: string;
  onboardingReminderHtml?: string;
  onboardingReminderOpened?: boolean;
  onboardingReminderClicked?: boolean;
  opened?: boolean;
  openedAt?: string;
  clicked?: boolean;
  clickedAt?: string;
  responded?: boolean;
  respondedAt?: string;
  noActionTaken?: boolean;
  noActionTakenAt?: string;
  dmSent?: boolean;
  dmSentAt?: string;
  called?: boolean;
  calledAt?: string;
  // Step 1 of declining: turned down the website service only — marketing
  // emails stay available. `declined` (step 2) = fully inactive.
  declinedWebsite?: boolean;
  declined?: boolean;
  declinedAt?: string;
  convertedToClient?: boolean;
  convertedToClientAt?: string;
  createdAt: string;
};

type EmailType = 'cold' | 'onboarding' | 'onboardingReminder' | 'outdatedMockup';

const EMAIL_TYPE_LABELS: Record<EmailType, string> = {
  cold: 'Cold Email',
  onboarding: 'Onboarding Email',
  onboardingReminder: 'Onboarding Reminder',
  outdatedMockup: 'Outdated Website Cold Email'
};

// Outdated and under-construction sites both get the website cold email
// (send-outdated-mockup); the server picks the opening line.
const needsNewWebsite = (lead: Pick<Lead, 'outdatedWebsite' | 'underConstructionWebsite'>) =>
  Boolean(lead.outdatedWebsite || lead.underConstructionWebsite);
const websiteEmailLabel = (lead: Lead) =>
  lead.underConstructionWebsite ? 'Website Cold Email' : 'Outdated Website Cold Email';

// Which cold-email variant a lead was actually sent is a fact about the
// past — it should never be re-derived from the lead's CURRENT website
// field, which can (and does) change afterward, e.g. once a lead becomes a
// real client with a site on file. Doing that made "See Sent ..." buttons
// relabel old sends incorrectly once a lead's website status changed after
// the fact (confirmed on a lead who was marked coldEmailSent manually —
// contacted outside the email flow, so there was never a coldEmailHtml/
// Subject snapshot to fall back on either — see coldEmailVariant's schema
// comment in server.js).
// Which pitch the NEXT cold email will be — mirrors pitchesMarketing() in
// server.js: marketing for leads with a website, Closed Web Dev Clients, and
// leads who declined the website service; the website pitch otherwise.
const nextPitchIsMarketing = (lead: Lead) => Boolean(lead.website || lead.closedWebDevClient || lead.declinedWebsite);

// Label for the cold email send/resend buttons, naming the pitch that will
// actually go out. "Send" (not "Resend") when it's a different pitch than the
// one they already got.
const coldEmailButtonLabel = (lead: Lead) => {
  const next = nextPitchIsMarketing(lead) ? 'Marketing' : 'Website';
  if (!lead.coldEmailSent) return `Send ${next} Cold Email`;
  const last = coldEmailVariantLabel(lead).trim();
  const sameAsLast = last === '' || last.startsWith(next);
  return `${sameAsLast ? 'Resend' : 'Send'} ${next} Cold Email`;
};

function coldEmailVariantLabel(lead: Lead): string {
  if (lead.coldEmailVariant) {
    // 'mockup' is the stored value for the website pitch (it used to offer a
    // mockup; it now says we built them a new website from scratch).
    return lead.coldEmailVariant === 'mockup' ? 'Website ' : 'Marketing / Ads ';
  }
  if (lead.coldEmailSubject) {
    // Older website-pitch subjects say "mockup", newer ones "new website".
    const subject = lead.coldEmailSubject.toLowerCase();
    return subject.includes('mockup') || subject.includes('new website') ? 'Website ' : 'Marketing / Ads ';
  }
  // coldEmailSubject wasn't always stored (older leads predate that field),
  // but the full sent HTML (coldEmailHtml) has been around longer, so check
  // that next before giving up. The marketing/ads pitch is the only variant
  // that ever mentions "ads" (as in "content and ads ideas") across all of
  // its inbound/outbound/newsletter wordings — the mockup pitch never uses
  // that word in any of its wordings.
  if (lead.coldEmailHtml) {
    // Match the marketing pitch's actual wording ("content and ads ideas",
    // "content ideas") — a bare "ads" also matches the "/crm/leads/" in every
    // tracked link, which made every email read as marketing.
    return /content (and ads )?ideas/i.test(lead.coldEmailHtml) ? 'Marketing / Ads ' : 'Website ';
  }
  // No stored record at all of what was actually sent — most likely a
  // manual "contacted outside the email flow" override with no snapshot to
  // check (see above). Guessing from live website status is what caused
  // this whole bug, so this stays neutral instead: plain "Cold Email," no
  // adjective, until someone sets coldEmailVariant explicitly in the edit
  // modal.
  return '';
}

type SentEmailData = {
  subject: string;
  html: string;
  sentAt: string | null;
  opened: boolean;
  openedAt: string | null;
  clicked: boolean;
  clickedAt: string | null;
  resendStatus: any;
};

type EditForm = {
  businessName: string;
  contactName: string;
  phone: string;
  instagram: string;
  email: string;
  website: string;
  outdatedWebsite: boolean;
  underConstructionWebsite: boolean;
  city: string;
  industry: string;
  notes: string;
  dmSent: boolean;
  closedWebDevClient: boolean;
};

const emptyEditForm: EditForm = {
  businessName: '',
  contactName: '',
  phone: '',
  instagram: '',
  email: '',
  website: '',
  outdatedWebsite: false,
  underConstructionWebsite: false,
  city: '',
  industry: '',
  notes: '',
  dmSent: false,
  closedWebDevClient: false
};

const editFormFromLead = (lead: Lead): EditForm => ({
  businessName: lead.businessName || '',
  contactName: lead.contactName || '',
  phone: lead.phone || '',
  instagram: lead.instagram || '',
  email: lead.email || '',
  website: lead.website || '',
  outdatedWebsite: lead.outdatedWebsite || false,
  underConstructionWebsite: lead.underConstructionWebsite || false,
  city: lead.city || '',
  industry: lead.industry || '',
  notes: lead.notes || '',
  dmSent: lead.dmSent || false,
  closedWebDevClient: lead.closedWebDevClient || false
});

type DirectionFilter = 'all' | 'inbound' | 'newsletter' | 'outbound';

// Existing leads created before "source" existed don't have it stored at
// all — fall back to the old inbound boolean so they still classify
// correctly instead of silently reading as "outbound".
// Inbound leads from before sources were recorded (2026-08-28) still carry
// the 'outbound' default — those were mockup requests (same rule as the
// server's ?source=mockup_form filter).
const leadSource = (lead: Lead): NonNullable<Lead['source']> =>
  lead.inbound && (!lead.source || lead.source === 'outbound') ? 'mockup_form' : lead.source || 'outbound';
type StatusFilter =
  | 'all'
  | 'not_contacted'
  | 'closed_client'
  | 'onboarding'
  | 'mockup_sent'
  | 'mockup_not_sent'
  | 'mockup_viewed'
  | 'mockup_clicked'
  | 'marketing_sent'
  | 'marketing_not_sent';

// Website pitch = a cold email that was the website variant, or the separate
// outdated-website email. Cold emails with no record of which pitch
// was sent (coldEmailVariantLabel returns '') match neither filter until
// their variant is set in Edit.
const sentMockupColdEmail = (lead: Lead) => lead.coldEmailSent && coldEmailVariantLabel(lead) === 'Website ';
const isMockupSent = (lead: Lead) => Boolean(sentMockupColdEmail(lead) || lead.outdatedMockupSent);
// Older leads recorded cold-email opens/clicks in the generic opened/clicked
// fields before coldEmailOpened/coldEmailClicked existed — count both.
const coldEmailOpened = (lead: Lead) => Boolean(lead.coldEmailOpened || lead.opened);
const coldEmailClicked = (lead: Lead) => Boolean(lead.coldEmailClicked || lead.clicked);
const isMockupViewed = (lead: Lead) =>
  Boolean((sentMockupColdEmail(lead) && coldEmailOpened(lead)) || lead.outdatedMockupOpened);
const isMockupClicked = (lead: Lead) =>
  Boolean((sentMockupColdEmail(lead) && coldEmailClicked(lead)) || lead.outdatedMockupClicked);
const isMarketingSent = (lead: Lead) => Boolean(lead.coldEmailSent && coldEmailVariantLabel(lead) === 'Marketing / Ads ');

// "Not Sent" filters only list leads you could actually send that email to
// right now: they have an address, aren't declined (fully), and aren't
// already a client / onboarding. Otherwise those filters were mostly leads
// with no email at all.
const canBeEmailed = (lead: Lead) =>
  Boolean(lead.email) && !lead.declined && !lead.convertedToClient && !lead.onboardingSent;
// Website pitch is possible when the next cold email would be the website
// pitch, or (for an outdated site) the outdated-website email.
const canGetWebsitePitch = (lead: Lead) =>
  !nextPitchIsMarketing(lead) ||
  Boolean(lead.website && needsNewWebsite(lead) && !lead.inbound && !lead.closedWebDevClient && !lead.declinedWebsite);
const websiteNotSent = (lead: Lead) => canBeEmailed(lead) && canGetWebsitePitch(lead) && !isMockupSent(lead);
const marketingNotSent = (lead: Lead) => canBeEmailed(lead) && nextPitchIsMarketing(lead) && !isMarketingSent(lead);
type EmailFilter = 'all' | 'has_email' | 'no_email';
type SortOption = 'newest' | 'oldest' | 'name';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;

const isNotContacted = (lead: Lead) =>
  !lead.declined && !lead.coldEmailSent && !lead.outdatedMockupSent && !lead.dmSent && !lead.called && !lead.onboardingSent;

const buildFindEmailUrl = (lead: Lead) => {
  const query = `${lead.businessName || ''} ${lead.city || ''} ("@gmail.com" OR "@outlook.com" OR "@hotmail.com" OR "@yahoo.com" OR "@icloud.com")`.trim();
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

const buildFindInstagramUrl = (lead: Lead) => {
  const query = `site:instagram.com ${lead.businessName || ''} ${lead.city || ''}`.trim();
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

// Instagram is stored as a bare handle (e.g. "joespizza" or "@joespizza"),
// not a full URL — build the profile link and a clean display label from it.
// Still handles old records that may have a full URL saved from before.
const instagramHandle = (value: string) => {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '');
  }
  return trimmed.replace(/^@/, '');
};

const buildInstagramProfileUrl = (value: string) => `https://instagram.com/${instagramHandle(value)}`;

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="#E1306C" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" stroke="#E1306C" strokeWidth="2" />
    <circle cx="17.7" cy="6.3" r="1.3" fill="#E1306C" />
  </svg>
);

export type LeadsTableHandle = { reload: () => void };
type LeadsTableProps = { defaultPageSize?: number };

const LeadsTable = forwardRef<LeadsTableHandle, LeadsTableProps>(({ defaultPageSize }, ref) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [viewingEmail, setViewingEmail] = useState<{ lead: Lead; type: EmailType } | null>(null);
  const [sentEmailData, setSentEmailData] = useState<SentEmailData | null>(null);
  const [loadingSentEmail, setLoadingSentEmail] = useState(false);
  const [sentEmailError, setSentEmailError] = useState('');

  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(emptyEditForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);
  const [editError, setEditError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [emailFilter, setEmailFilter] = useState<EmailFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [industryOptions, setIndustryOptions] = useState<string[]>(DEFAULT_INDUSTRY_OPTIONS);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/leads`);
      if (response.data?.ok) {
        setLeads(response.data.leads || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setError('Could not load leads.');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/leads/industries`);
      if (response.data?.ok) setIndustryOptions(response.data.industries || DEFAULT_INDUSTRY_OPTIONS);
    } catch (fetchError) {
      console.error(fetchError);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchIndustryOptions();
  }, []);

  useImperativeHandle(ref, () => ({ reload: fetchLeads }));

  const categoryOptions = useMemo(() => {
    const categories = new Set<string>();
    leads.forEach((lead) => {
      if (lead.industry) categories.add(lead.industry);
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  // Every rate below is computed against the leads that actually got that
  // email, not against every lead — "half of onboarding emails opened" only
  // makes sense relative to onboarding emails sent.
  const leadAnalytics = useMemo(() => {
    const rateRow = (label: string, sent: Lead[], openedField?: keyof Lead, clickedField?: keyof Lead) => {
      const opened = openedField ? sent.filter((l) => l[openedField]).length : null;
      const clicked = clickedField ? sent.filter((l) => l[clickedField]).length : null;
      return {
        label,
        sentCount: sent.length,
        openedCount: opened,
        openedRate: opened !== null && sent.length ? opened / sent.length : null,
        clickedCount: clicked,
        clickedRate: clicked !== null && sent.length ? clicked / sent.length : null
      };
    };

    const mockupCold = leads.filter((l) => l.coldEmailSent && coldEmailVariantLabel(l) === 'Website ');
    const marketingCold = leads.filter((l) => l.coldEmailSent && coldEmailVariantLabel(l) === 'Marketing / Ads ');
    const outdatedMockup = leads.filter((l) => l.outdatedMockupSent);
    const onboardingEmails = leads.filter((l) => l.onboardingSent);
    const reminderEmails = leads.filter((l) => l.reminderEmailSent);

    const rows = [
      rateRow('Website Cold Email', mockupCold, 'coldEmailOpened', 'coldEmailClicked'),
      rateRow('Marketing / Ads Cold Email', marketingCold, 'coldEmailOpened', 'coldEmailClicked'),
      rateRow('Outdated Website Cold Email', outdatedMockup, 'outdatedMockupOpened', 'outdatedMockupClicked'),
      rateRow('Onboarding Email', onboardingEmails, 'onboardingOpened', 'onboardingClicked'),
      // Reminder emails reuse the original cold email's own tracking fields
      // (same "cold" link type) rather than having their own, so there's no
      // separate open/click number to attribute specifically to the reminder.
      rateRow('Reminder Email', reminderEmails)
    ];

    const totalColdSent = mockupCold.length + marketingCold.length;
    const totalColdOpened = [...mockupCold, ...marketingCold].filter((l) => l.coldEmailOpened).length;
    const totalColdClicked = [...mockupCold, ...marketingCold].filter((l) => l.coldEmailClicked).length;

    return {
      rows,
      total: leads.length,
      onboardedCount: leads.filter((l) => l.convertedToClient).length,
      declinedCount: leads.filter((l) => l.declined).length,
      noActionCount: leads.filter((l) => l.noActionTaken).length,
      respondedCount: leads.filter((l) => l.responded).length,
      respondedRate: totalColdSent ? leads.filter((l) => l.responded).length / totalColdSent : 0,
      overallOpenRate: totalColdSent ? totalColdOpened / totalColdSent : 0,
      overallClickRate: totalColdSent ? totalColdClicked / totalColdSent : 0,
      bySource: {
        outbound: leads.filter((l) => leadSource(l) === 'outbound').length,
        mockup_form: leads.filter((l) => leadSource(l) === 'mockup_form').length,
        newsletter: leads.filter((l) => leadSource(l) === 'newsletter').length
      }
    };
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = leads.filter((lead) => {
      const matchesQuery =
        !q ||
        (lead.businessName || '').toLowerCase().includes(q) ||
        (lead.contactName || '').toLowerCase().includes(q) ||
        (lead.email || '').toLowerCase().includes(q) ||
        (lead.phone || '').toLowerCase().includes(q) ||
        (lead.city || '').toLowerCase().includes(q);

      const matchesDirection =
        directionFilter === 'all' ||
        (directionFilter === 'inbound' && (leadSource(lead) === 'mockup_form' || leadSource(lead) === 'free_audit_form')) ||
        (directionFilter === 'newsletter' && leadSource(lead) === 'newsletter') ||
        (directionFilter === 'outbound' && leadSource(lead) === 'outbound');

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'not_contacted' && isNotContacted(lead)) ||
        (statusFilter === 'closed_client' && lead.closedWebDevClient) ||
        (statusFilter === 'onboarding' && lead.onboardingSent) ||
        (statusFilter === 'mockup_sent' && isMockupSent(lead)) ||
        (statusFilter === 'mockup_not_sent' && websiteNotSent(lead)) ||
        (statusFilter === 'mockup_viewed' && isMockupViewed(lead)) ||
        (statusFilter === 'mockup_clicked' && isMockupClicked(lead)) ||
        (statusFilter === 'marketing_sent' && isMarketingSent(lead)) ||
        (statusFilter === 'marketing_not_sent' && marketingNotSent(lead));

      const matchesEmail =
        emailFilter === 'all' ||
        (emailFilter === 'has_email' && Boolean(lead.email)) ||
        (emailFilter === 'no_email' && !lead.email);

      const matchesCategory = categoryFilter === 'all' || lead.industry === categoryFilter;

      return matchesQuery && matchesDirection && matchesStatus && matchesEmail && matchesCategory;
    });

    result = [...result].sort((a, b) => {
      if (sortOption === 'name') {
        return (a.businessName || '').localeCompare(b.businessName || '');
      }
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortOption === 'oldest' ? aTime - bTime : bTime - aTime;
    });

    return result;
  }, [leads, searchQuery, directionFilter, statusFilter, emailFilter, categoryFilter, sortOption]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, directionFilter, statusFilter, emailFilter, categoryFilter, sortOption, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedLeads = useMemo(
    () => filteredLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredLeads, currentPage, pageSize]
  );

  const runAction = async (lead: Lead, action: () => Promise<void>) => {
    setBusyId(lead._id);
    setMessage('');
    setError('');
    try {
      await action();
    } finally {
      setBusyId(null);
    }
  };

  const handleSendColdEmail = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/crm/leads/${lead._id}/send-cold-email`);
        if (response.data?.ok) {
          setMessage(`Cold email sent to ${lead.businessName || lead.email}.`);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not send cold email.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not send cold email.');
      }
    });

  const handleSendReminderEmail = (lead: Lead) => {
    const who = lead.businessName || lead.email || 'this lead';
    const confirmSend = window.confirm(
      lead.reminderEmailSent
        ? `Resend the reminder email to ${who}? They already received one before.`
        : `Send the reminder email to ${who}?`
    );
    if (!confirmSend) return;
    runAction(lead, async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/crm/leads/${lead._id}/send-reminder-email`);
        if (response.data?.ok) {
          setMessage(`Reminder email sent to ${lead.businessName || lead.email}.`);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not send reminder email.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not send reminder email.');
      }
    });
  };

  const handleSendOutdatedMockup = (lead: Lead) => {
    if (!lead.website || !needsNewWebsite(lead)) {
      setError('This email only applies to leads with a website flagged as outdated or under construction.');
      return;
    }

    const confirmSend = window.confirm(
      `Send the ${websiteEmailLabel(lead).toLowerCase()} to ${lead.businessName || lead.email || 'this lead'}?`
    );
    if (!confirmSend) return;

    runAction(lead, async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/crm/leads/${lead._id}/send-outdated-mockup`);
        if (response.data?.ok) {
          setMessage(`${websiteEmailLabel(lead)} sent to ${lead.businessName || lead.email}.`);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not send outdated website cold email.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not send outdated website cold email.');
      }
    });
  };

  const handleSendOnboarding = (lead: Lead) => {
    const confirmSend = window.confirm(
      `Send the onboarding email to ${lead.businessName || lead.email || 'this lead'}?`
    );
    if (!confirmSend) return;

    runAction(lead, async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/crm/leads/${lead._id}/send-onboarding`);
        if (response.data?.ok) {
          setMessage(`Onboarding email sent to ${lead.businessName || lead.email}.`);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not send onboarding email.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not send onboarding email.');
      }
    });
  };

  const handleSendOnboardingReminder = (lead: Lead) => {
    const who = lead.businessName || lead.email || 'this lead';
    const confirmSend = window.confirm(
      lead.onboardingReminderSent
        ? `Resend the onboarding reminder to ${who}? They already received one before.`
        : `Send the onboarding reminder to ${who}?`
    );
    if (!confirmSend) return;
    runAction(lead, async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/crm/leads/${lead._id}/send-onboarding-reminder`);
        if (response.data?.ok) {
          setMessage(`Onboarding reminder sent to ${lead.businessName || lead.email}.`);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not send onboarding reminder.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not send onboarding reminder.');
      }
    });
  };

  // Two-step decline: "Decline Website Service" (website pitches stop,
  // marketing still allowed) → "Decline Marketing Services" (fully
  // inactive). Each step can be undone on its own.
  const updateDecline = (lead: Lead, body: { declinedWebsite?: boolean; declined?: boolean }, success: string) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/decline`, body);
        if (response.data?.ok) {
          setMessage(success);
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update decline status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update decline status.');
      }
    });

  const handleDeclineWebsite = (lead: Lead) => {
    const name = lead.businessName || lead.email || 'this lead';
    if (!window.confirm(`Mark ${name} as declined for website services? Website emails stop — marketing emails stay available.`)) return;
    updateDecline(lead, { declinedWebsite: true }, 'Marked as declined website service — marketing emails still available.');
  };

  const handleDeclineMarketing = (lead: Lead) => {
    const name = lead.businessName || lead.email || 'this lead';
    if (!window.confirm(`Mark ${name} as declined for marketing services too? They'll be inactive and you won't be able to email them.`)) return;
    updateDecline(lead, { declined: true }, 'Lead marked as declined / inactive.');
  };

  const handleToggleDm = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/dm-sent`, { dmSent: !lead.dmSent });
        if (response.data?.ok) {
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update DM status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update DM status.');
      }
    });

  const handleToggleCalled = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/called`, { called: !lead.called });
        if (response.data?.ok) {
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update called status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update called status.');
      }
    });

  const handleToggleNoAction = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/no-action`, { noActionTaken: !lead.noActionTaken });
        if (response.data?.ok) {
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update no-action status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update no-action status.');
      }
    });

  const handleToggleInstagramNotFound = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/instagram-not-found`, {
          instagramNotFound: !lead.instagramNotFound
        });
        if (response.data?.ok) {
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update Instagram search status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update Instagram search status.');
      }
    });

  const handleToggleEmailNotFound = (lead: Lead) =>
    runAction(lead, async () => {
      try {
        const response = await axios.patch(`${API_BASE_URL}/crm/leads/${lead._id}/email-not-found`, {
          emailNotFound: !lead.emailNotFound
        });
        if (response.data?.ok) {
          fetchLeads();
        } else {
          setError(response.data?.message || 'Could not update email search status.');
        }
      } catch (actionError) {
        console.error(actionError);
        setError('Could not update email search status.');
      }
    });

  const handleViewSentEmail = async (lead: Lead, type: EmailType) => {
    setViewingEmail({ lead, type });
    setSentEmailData(null);
    setSentEmailError('');
    setLoadingSentEmail(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/leads/${lead._id}/sent-email`, { params: { type } });
      if (response.data?.ok) {
        setSentEmailData(response.data);
      } else {
        setSentEmailError(response.data?.message || 'Could not load the sent email.');
      }
    } catch (fetchError) {
      console.error(fetchError);
      setSentEmailError('Could not load the sent email.');
    } finally {
      setLoadingSentEmail(false);
    }
  };

  const closeSentEmailModal = () => {
    setViewingEmail(null);
    setSentEmailData(null);
    setSentEmailError('');
  };

  const openEditModal = (lead: Lead) => {
    setEditingLead(lead);
    setEditForm(editFormFromLead(lead));
    setEditError('');
  };

  const closeEditModal = () => {
    setEditingLead(null);
    setEditForm(emptyEditForm);
    setEditError('');
  };

  const handleSaveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingLead) return;

    setEditSaving(true);
    setEditError('');
    try {
      const response = await axios.put(`${API_BASE_URL}/crm/leads/${editingLead._id}`, editForm);
      if (response.data?.ok) {
        setMessage('Lead updated.');
        closeEditModal();
        fetchLeads();
        fetchIndustryOptions();
      } else {
        setEditError(response.data?.message || 'Could not update lead.');
      }
    } catch (saveError) {
      console.error(saveError);
      setEditError('Could not update lead.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!editingLead) return;

    const confirmDelete = window.confirm(
      `Delete ${editingLead.businessName || editingLead.email || 'this lead'}? This cannot be undone.`
    );
    if (!confirmDelete) return;

    setEditDeleting(true);
    setEditError('');
    try {
      const response = await axios.delete(`${API_BASE_URL}/crm/leads/${editingLead._id}`);
      if (response.data?.ok) {
        setMessage('Lead deleted.');
        closeEditModal();
        fetchLeads();
      } else {
        setEditError(response.data?.message || 'Could not delete lead.');
      }
    } catch (deleteError) {
      console.error(deleteError);
      setEditError('Could not delete lead.');
    } finally {
      setEditDeleting(false);
    }
  };

  return (
    <div>
      {message ? <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert> : null}
      {error ? <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert> : null}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            placeholder="Search by business, contact, email, phone..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select value={directionFilter} onChange={(event) => setDirectionFilter(event.target.value as DirectionFilter)}>
            <option value="all">All Leads</option>
            <option value="inbound">Inbound</option>
            <option value="newsletter">Newsletter</option>
            <option value="outbound">Outbound</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
            <option value="all">Any Contact Status</option>
            <option value="not_contacted">Not Contacted</option>
            <option value="closed_client">Closed Web Dev Client</option>
            <option value="onboarding">Onboarding Sent</option>
            <option value="mockup_sent">Website Cold Email Sent</option>
            <option value="mockup_not_sent">Website Cold Email Not Sent</option>
            <option value="mockup_viewed">Viewed Website Cold Email</option>
            <option value="mockup_clicked">Clicked Website Cold Email</option>
            <option value="marketing_sent">Marketing Cold Email Sent</option>
            <option value="marketing_not_sent">Marketing Cold Email Not Sent</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select value={sortOption} onChange={(event) => setSortOption(event.target.value as SortOption)}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Business Name A–Z</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Select value={emailFilter} onChange={(event) => setEmailFilter(event.target.value as EmailFilter)}>
            <option value="all">Any Email Status</option>
            <option value="has_email">Has Email</option>
            <option value="no_email">No Email</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value="all">All Categories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {!loading && filteredLeads.length > 0 ? (
        <Row className="mb-2 align-items-center">
          <Col style={{ color: '#aaa', fontSize: '0.85rem' }}>
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredLeads.length)} of {filteredLeads.length}
          </Col>
          <Col xs="auto">
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              style={{ width: 'auto' }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      ) : null}

      {loading ? <p style={{ color: '#d4d4d4' }}>Loading leads...</p> : null}
      {!loading && filteredLeads.length === 0 ? <Alert variant="secondary">No leads match.</Alert> : null}

      {!loading && filteredLeads.length > 0 ? (
        <Table striped bordered hover variant="dark" responsive>
          <thead>
            <tr>
              <th>Business</th>
              <th>Contact</th>
              <th>Phone</th>
              <th>Category</th>
              <th>Instagram</th>
              <th>Cold DM'd</th>
              <th>Called</th>
              <th>Direction</th>
              <th>Status</th>
              <th>Engagement</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLeads.map((lead) => {
              const busy = busyId === lead._id;
              return (
                <tr key={lead._id} style={lead.declined ? { opacity: 0.5 } : undefined}>
                  <td>{lead.businessName || '—'}</td>
                  <td>
                    <div>{lead.contactName || '—'}</div>
                    {lead.email ? (
                      <small style={{ color: '#aaa' }}>{lead.email}</small>
                    ) : lead.emailNotFound ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <small style={{ color: '#666' }}>No email found</small>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 text-start"
                          style={{ fontSize: '0.75rem' }}
                          disabled={busy}
                          onClick={() => handleToggleEmailNotFound(lead)}
                        >
                          Undo
                        </Button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => window.open(buildFindEmailUrl(lead), '_blank', 'noopener,noreferrer')}
                        >
                          Find Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={busy}
                          onClick={() => handleToggleEmailNotFound(lead)}
                        >
                          No Email Found
                        </Button>
                      </div>
                    )}
                  </td>
                  <td>{lead.phone || '—'}</td>
                  <td>{lead.industry || '—'}</td>
                  <td>
                    {lead.instagram ? (
                      <a
                        href={buildInstagramProfileUrl(lead.instagram)}
                        target="_blank"
                        rel="noreferrer"
                        title={`@${instagramHandle(lead.instagram)}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        className="text-white"
                      >
                        <InstagramIcon />
                        <small style={{ color: '#aaa' }}>@{instagramHandle(lead.instagram)}</small>
                      </a>
                    ) : lead.instagramNotFound ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <small style={{ color: '#666' }}>No IG found</small>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 text-start"
                          style={{ fontSize: '0.75rem' }}
                          disabled={busy}
                          onClick={() => handleToggleInstagramNotFound(lead)}
                        >
                          Undo
                        </Button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => window.open(buildFindInstagramUrl(lead), '_blank', 'noopener,noreferrer')}
                        >
                          Find IG
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={busy}
                          onClick={() => handleToggleInstagramNotFound(lead)}
                        >
                          No IG Found
                        </Button>
                      </div>
                    )}
                    {lead.website ? (
                      <div style={{ marginTop: '4px' }}>
                        <a href={externalUrl(lead.website)} target="_blank" rel="noreferrer" className="text-white">
                          <small style={{ color: '#aaa' }}>
                            {lead.website.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}
                          </small>
                        </a>
                        {lead.outdatedWebsite ? (
                          <div><small style={{ color: '#888' }}>Outdated</small></div>
                        ) : null}
                        {lead.underConstructionWebsite ? (
                          <div><small style={{ color: '#888' }}>Under construction</small></div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {lead.instagram ? (
                      <Button
                        size="sm"
                        variant={lead.dmSent ? 'success' : 'outline-secondary'}
                        disabled={busy}
                        onClick={() => handleToggleDm(lead)}
                      >
                        {lead.dmSent ? 'DM Sent' : 'Not DM\'d'}
                      </Button>
                    ) : (
                      <small style={{ color: '#666' }}>No IG</small>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant={lead.called ? 'success' : 'warning'}
                      disabled={busy}
                      onClick={() => handleToggleCalled(lead)}
                    >
                      {lead.called ? 'Called' : 'Not Called'}
                    </Button>
                  </td>
                  <td>
                    <Badge bg={lead.inbound ? 'info' : 'secondary'}>{lead.inbound ? 'Inbound' : 'Outbound'}</Badge>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {lead.convertedToClient ? <Badge bg="success">WEB CLIENT</Badge> : null}
                      {lead.closedWebDevClient ? <Badge bg="primary">Closed Web Dev Client</Badge> : null}
                      {lead.declined ? (
                        <Badge bg="danger">Declined / Inactive</Badge>
                      ) : lead.declinedWebsite ? (
                        <Badge bg="warning" text="dark">Declined Website</Badge>
                      ) : null}
                      {isNotContacted(lead) && !lead.declined && !lead.convertedToClient ? <Badge bg="secondary">Not Contacted</Badge> : null}
                      {lead.coldEmailSent ? <Badge bg="success">Cold Email Sent</Badge> : null}
                      {lead.onboardingSent ? <Badge bg="success">Onboarding Sent</Badge> : null}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.8rem' }}>
                      <span style={{ color: lead.opened ? '#68FF00' : '#666' }}>{lead.opened ? '✓ Opened' : 'Not opened'}</span>
                      <span style={{ color: lead.clicked ? '#68FF00' : '#666' }}>{lead.clicked ? '✓ Clicked' : 'Not clicked'}</span>
                      <span style={{ color: lead.responded ? '#68FF00' : '#666' }}>{lead.responded ? '✓ Responded' : 'No response'}</span>
                      {lead.noActionTaken ? <span style={{ color: '#ff4d4d' }}>✗ No Action Taken</span> : null}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' }}>
                      {lead.convertedToClient ? (
                        <small style={{ color: '#aaa' }}>Also a WEB CLIENT — manage subscription/payment contact in Website Clients below.</small>
                      ) : null}
                      {lead.email ? (
                          <>
                            {!lead.onboardingSent ? (
                              lead.coldEmailSent ? (
                                <>
                                  {lead.coldEmailHtml ? (
                                    <Button size="sm" variant="outline-light" onClick={() => handleViewSentEmail(lead, 'cold')}>
                                      See Sent {coldEmailVariantLabel(lead)}Cold Email
                                    </Button>
                                  ) : (
                                    // Marked sent manually (contacted outside the email flow) —
                                    // there's no snapshot to view, so don't offer a button that
                                    // can only ever 404.
                                    <small style={{ color: '#888' }}>
                                      {coldEmailVariantLabel(lead)}Cold Email marked sent — no copy on
                                      file (sent outside the email flow; use Edit to label which
                                      pitch it was)
                                    </small>
                                  )}
                                  {lead.noActionTaken || (leadSource(lead) === 'newsletter' && lead.responded && !lead.coldEmailClicked) ? (
                                    <Button
                                      size="sm"
                                      variant="outline-warning"
                                      disabled={busy || lead.declined}
                                      onClick={() => handleSendReminderEmail(lead)}
                                    >
                                      {lead.reminderEmailSent ? 'Resend Reminder Email' : 'Send Reminder Email'}
                                    </Button>
                                  ) : !lead.coldEmailClicked ? (
                                    <Button
                                      size="sm"
                                      variant="outline-warning"
                                      disabled={busy || lead.declined}
                                      onClick={() => {
                                        const confirmResend = window.confirm(
                                          `${coldEmailButtonLabel(lead)} to ${lead.businessName || lead.email || 'this lead'}? They already received a cold email before.`
                                        );
                                        if (confirmResend) handleSendColdEmail(lead);
                                      }}
                                    >
                                      {coldEmailButtonLabel(lead)}
                                    </Button>
                                  ) : (
                                    // Clicked but no booking yet — keep every follow-up available.
                                    <>
                                      <small style={{ color: '#aaa' }}>Clicked — hasn&apos;t booked yet</small>
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        disabled={busy || lead.declined}
                                        onClick={() => {
                                          const confirmResend = window.confirm(
                                            `${coldEmailButtonLabel(lead)} to ${lead.businessName || lead.email || 'this lead'}? They already clicked the last one but haven't booked.`
                                          );
                                          if (confirmResend) handleSendColdEmail(lead);
                                        }}
                                      >
                                        {coldEmailButtonLabel(lead)}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        disabled={busy || lead.declined}
                                        onClick={() => handleSendReminderEmail(lead)}
                                      >
                                        {lead.reminderEmailSent ? 'Resend Reminder Email' : 'Send Reminder Email'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={busy || lead.declined}
                                        onClick={() => handleToggleNoAction(lead)}
                                      >
                                        No Action
                                      </Button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <Button size="sm" variant="outline-warning" disabled={busy || lead.declined} onClick={() => handleSendColdEmail(lead)}>
                                  {coldEmailButtonLabel(lead)}
                                </Button>
                              )
                            ) : null}
                            {/* Outdated website pitch — never for a Closed Web Dev Client (their
                                site is already being built); they only get marketing emails. */}
                            {!lead.inbound && lead.website && needsNewWebsite(lead) && !lead.closedWebDevClient && !lead.declinedWebsite ? (
                              lead.outdatedMockupSent ? (
                                <>
                                  <Button size="sm" variant="outline-light" onClick={() => handleViewSentEmail(lead, 'outdatedMockup')}>
                                    See Sent {websiteEmailLabel(lead)}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    disabled={busy || lead.declined}
                                    onClick={() => {
                                      const confirmResend = window.confirm(
                                        `Resend the ${websiteEmailLabel(lead).toLowerCase()} to ${lead.businessName || lead.email || 'this lead'}? They already received one before.`
                                      );
                                      if (confirmResend) handleSendOutdatedMockup(lead);
                                    }}
                                  >
                                    Resend {websiteEmailLabel(lead)}
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" variant="outline-warning" disabled={busy || lead.declined} onClick={() => handleSendOutdatedMockup(lead)}>
                                  Send {websiteEmailLabel(lead)}
                                </Button>
                              )
                            ) : null}
                            {/* A Closed Web Dev Client always gets onboarding, even if
                                they already have a (non-outdated) site on file. */}
                            {lead.closedWebDevClient || !lead.website || needsNewWebsite(lead) ? (
                              lead.onboardingSent ? (
                                <>
                                  <Button size="sm" variant="outline-light" onClick={() => handleViewSentEmail(lead, 'onboarding')}>
                                    See Sent Onboarding Email
                                  </Button>
                                  <Button size="sm" variant="outline-warning" disabled={busy || lead.declined} onClick={() => handleSendOnboardingReminder(lead)}>
                                    {lead.onboardingReminderSent ? 'Resend Reminder' : 'Send Reminder'}
                                  </Button>
                                </>
                              ) : lead.closedWebDevClient ? (
                                <Button size="sm" variant="outline-success" disabled={busy || lead.declined} onClick={() => handleSendOnboarding(lead)}>
                                  Send Onboarding
                                </Button>
                              ) : null
                            ) : null}
                          </>
                        ) : null}
                        <Button size="sm" variant="outline-light" onClick={() => openEditModal(lead)}>
                          Edit
                        </Button>
                        {lead.declined ? (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={busy}
                            onClick={() => updateDecline(lead, { declined: false }, 'Lead reactivated — marketing emails available again.')}
                          >
                            Undo Inactive
                          </Button>
                        ) : lead.declinedWebsite ? (
                          <>
                            <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => handleDeclineMarketing(lead)}>
                              Decline Marketing Services
                            </Button>
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 text-start"
                              style={{ fontSize: '0.75rem', color: '#888' }}
                              disabled={busy}
                              onClick={() => updateDecline(lead, { declinedWebsite: false }, 'Website decline undone.')}
                            >
                              Undo website decline
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => handleDeclineWebsite(lead)}>
                            Decline Website Service
                          </Button>
                        )}
                      </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : null}

      {!loading && totalPages > 1 ? (
        <Pagination className="justify-content-center mt-3">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} />
          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2)
            .map((page, index, pages) => (
              <span key={page} style={{ display: 'contents' }}>
                {index > 0 && pages[index - 1] !== page - 1 ? <Pagination.Ellipsis disabled /> : null}
                <Pagination.Item active={page === currentPage} onClick={() => setCurrentPage(page)}>
                  {page}
                </Pagination.Item>
              </span>
            ))}
          <Pagination.Next onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      ) : null}

      <h2 style={{ color: '#68FF00', marginTop: '2.5rem', marginBottom: '1rem' }}>Lead Analytics</h2>
      <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.25rem' }}>
        <Card.Body>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Total Leads</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{leadAnalytics.total}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Onboarded</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#68FF00' }}>{leadAnalytics.onboardedCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Responded</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#38bdf8' }}>
                {leadAnalytics.respondedCount} <small style={{ fontSize: '0.9rem', color: '#aaa' }}>({(leadAnalytics.respondedRate * 100).toFixed(0)}% of cold emails sent)</small>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>No Action Taken</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff4d4d' }}>{leadAnalytics.noActionCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Declined</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#666' }}>{leadAnalytics.declinedCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Overall Open / Click Rate</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {(leadAnalytics.overallOpenRate * 100).toFixed(0)}% <small style={{ fontSize: '0.9rem', color: '#aaa' }}>/</small> {(leadAnalytics.overallClickRate * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <Badge bg="secondary">Outbound: {leadAnalytics.bySource.outbound}</Badge>
            <Badge bg="secondary">Inbound (Mockup Form): {leadAnalytics.bySource.mockup_form}</Badge>
            <Badge bg="secondary">Newsletter: {leadAnalytics.bySource.newsletter}</Badge>
          </div>

          <Table striped bordered hover variant="dark" responsive size="sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Email Type</th>
                <th>Sent</th>
                <th>Opened</th>
                <th>Clicked</th>
              </tr>
            </thead>
            <tbody>
              {leadAnalytics.rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.sentCount}</td>
                  <td>
                    {row.openedCount === null ? (
                      <span style={{ color: '#666' }}>—</span>
                    ) : (
                      <span style={{ color: '#68FF00' }}>
                        {row.openedCount} <small style={{ color: '#aaa' }}>({((row.openedRate || 0) * 100).toFixed(0)}%)</small>
                      </span>
                    )}
                  </td>
                  <td>
                    {row.clickedCount === null ? (
                      <span style={{ color: '#666' }}>—</span>
                    ) : (
                      <span style={{ color: '#38bdf8' }}>
                        {row.clickedCount} <small style={{ color: '#aaa' }}>({((row.clickedRate || 0) * 100).toFixed(0)}%)</small>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={Boolean(viewingEmail)} onHide={closeSentEmailModal} size="lg" centered>
        <Modal.Header style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
          <Modal.Title>
            {viewingEmail ? `${EMAIL_TYPE_LABELS[viewingEmail.type]} — ${viewingEmail.lead.businessName || viewingEmail.lead.email}` : ''}
          </Modal.Title>
          <button type="button" className="btn-close btn-close-danger" aria-label="Close" onClick={closeSentEmailModal} />
        </Modal.Header>
        <Modal.Body style={{ background: '#111', color: 'white' }}>
          {loadingSentEmail ? <p style={{ color: '#d4d4d4' }}>Loading...</p> : null}
          {sentEmailError ? <Alert variant="danger">{sentEmailError}</Alert> : null}
          {sentEmailData ? (
            <>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Sent</div>
                  <div style={{ color: '#ccc' }}>
                    {sentEmailData.sentAt ? new Date(sentEmailData.sentAt).toLocaleString() : 'Unknown'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Opened</div>
                  <div style={{ color: sentEmailData.opened ? '#68FF00' : '#ccc' }}>
                    {sentEmailData.opened ? `✓ Yes — ${sentEmailData.openedAt ? new Date(sentEmailData.openedAt).toLocaleString() : ''}` : 'Not opened'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Clicked</div>
                  <div style={{ color: sentEmailData.clicked ? '#68FF00' : '#ccc' }}>
                    {sentEmailData.clicked ? `✓ Yes — ${sentEmailData.clickedAt ? new Date(sentEmailData.clickedAt).toLocaleString() : ''}` : 'Not clicked'}
                  </div>
                </div>
                {sentEmailData.resendStatus ? (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase' }}>Resend Status</div>
                    <div style={{ color: '#ccc' }}>{sentEmailData.resendStatus.last_event || 'Unknown'}</div>
                  </div>
                ) : null}
              </div>
              <p style={{ color: '#d4d4d4', marginBottom: '0.5rem' }}><strong>Subject:</strong> {sentEmailData.subject}</p>
              <div style={{ border: '1px solid #2b2b2b', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                <iframe
                  title="Sent email preview"
                  srcDoc={sentEmailData.html}
                  style={{ width: '100%', height: '500px', border: 'none' }}
                  sandbox=""
                />
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
          <Button variant="outline-light" size="sm" onClick={closeSentEmailModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(editingLead)} onHide={closeEditModal} centered>
        <Form onSubmit={handleSaveEdit}>
          <Modal.Header closeButton style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
            <Modal.Title>Edit Lead</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#111', color: 'white' }}>
            {editError ? <Alert variant="danger">{editError}</Alert> : null}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Business Name</Form.Label>
                  <Form.Control value={editForm.businessName} onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Contact / Owner Name</Form.Label>
                  <Form.Control value={editForm.contactName} onChange={(e) => setEditForm({ ...editForm, contactName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <Form.Control value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Instagram</Form.Label>
                  <Form.Control value={editForm.instagram} onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })} placeholder="handle or URL" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Website</Form.Label>
                  <Form.Control value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://..." />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Website is outdated"
                    checked={editForm.outdatedWebsite}
                    onChange={(e) => setEditForm({ ...editForm, outdatedWebsite: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Website is under construction"
                    checked={editForm.underConstructionWebsite}
                    onChange={(e) => setEditForm({ ...editForm, underConstructionWebsite: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="DM sent"
                    checked={editForm.dmSent}
                    onChange={(e) => setEditForm({ ...editForm, dmSent: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Closed Web Dev Client (deal made — unlocks Send Onboarding)"
                    checked={editForm.closedWebDevClient}
                    onChange={(e) => setEditForm({ ...editForm, closedWebDevClient: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>City</Form.Label>
                  <Form.Control value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} placeholder="e.g. Miami, FL" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Industry</Form.Label>
                  <Form.Control
                    value={editForm.industry}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                    placeholder="Select or type a new industry"
                    list="lead-industry-options"
                  />
                  <datalist id="lead-industry-options">
                    {industryOptions.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-2">
                  <Form.Label>Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
            <Button
              variant="outline-danger"
              size="sm"
              className="me-auto"
              onClick={handleDeleteLead}
              disabled={editSaving || editDeleting}
            >
              {editDeleting ? 'Deleting...' : 'Delete Business'}
            </Button>
            <Button variant="outline-light" size="sm" onClick={closeEditModal} disabled={editSaving || editDeleting}>Cancel</Button>
            <Button variant="success" size="sm" type="submit" disabled={editSaving || editDeleting}>
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
});

export default LeadsTable;
